import { isRoundArchived } from "@engine/round/accessors";
import type { RoundAggregate } from "@engine/round/aggregate";
import { ARCHIVED_ROUND_ERROR } from "@engine/round/permissions";
import type { AppMode } from "@lib/types";

/**
 * Why this client can't edit the round right now, or null when it can.
 *
 * One derived value, computed once per store snapshot from facts the store
 * already holds, so every editing surface asks the same question the same
 * way and the dispatch chokepoint refuses the same edits the UI disables.
 * Reasons are round-wide; per-track locks (evals freeze, complete) stay with
 * `getViewerMvfEditLocks` and sit *below* this in a surface's reason ladder.
 *
 * The two lock values are module constants so a selector returning one keeps
 * a stable identity across snapshots.
 */
export type EditLockReason = "archived" | "completed" | "disconnected";

export type EditLock = {
  reason: EditLockReason;
  /** User-facing: what is locked and, where there is one, the way out. */
  message: string;
};

export const ARCHIVED_EDIT_LOCK: EditLock = Object.freeze({
  reason: "archived",
  message: ARCHIVED_ROUND_ERROR,
});

export const COMPLETED_EDIT_LOCK: EditLock = Object.freeze({
  reason: "completed",
  message: "This round is completed. Reopen it in Round State to make changes.",
});

export const DISCONNECTED_EDIT_LOCK: EditLock = Object.freeze({
  reason: "disconnected",
  message:
    "This round is no longer syncing, so it can't be edited. Reload to reconnect.",
});

export function getEditLock(
  round: Pick<RoundAggregate, "archivedAt" | "completedAt"> | null,
  mode: Pick<AppMode, "disconnected">,
): EditLock | null {
  // Disconnection outranks the archive: a client whose socket is gone for
  // good can't do anything — not even restore — and its way out is a reload.
  if (mode.disconnected) {
    return DISCONNECTED_EDIT_LOCK;
  }

  if (round != null && isRoundArchived(round)) {
    return ARCHIVED_EDIT_LOCK;
  }

  if (round?.completedAt != null) {
    return COMPLETED_EDIT_LOCK;
  }

  return null;
}

/**
 * Props that disable a `@sfc/ui` Button with the lock's message as its
 * tooltip, or nothing when there is no lock. Pure and identity-stable per
 * lock (the locks are module constants), so the result is safe to use as a
 * memo dependency. Spread it AFTER the control's own `disabled` /
 * `disabledReason`: JSX props apply in order, and the lock has to win.
 */
export type EditLockButtonProps =
  | { disabled: true; disabledReason: string }
  | Record<string, never>;

const NO_LOCK_PROPS: EditLockButtonProps = Object.freeze({});
const lockPropsCache = new WeakMap<EditLock, EditLockButtonProps>();

export function editLockButtonProps(
  lock: EditLock | null,
): EditLockButtonProps {
  if (lock == null) {
    return NO_LOCK_PROPS;
  }

  const cached = lockPropsCache.get(lock);

  if (cached) {
    return cached;
  }

  const props: EditLockButtonProps = Object.freeze({
    disabled: true as const,
    disabledReason: lock.message,
  });
  lockPropsCache.set(lock, props);
  return props;
}

/**
 * The error a locked dispatch returns. `dispatch` toasts the lock's message
 * itself, so callers that toast their own failures check for this class and
 * stay quiet — the old idiom was matching the message text.
 */
/**
 * React `key` for a component that holds a local draft (a form, a typed
 * score, a slider position). Keying on it remounts the component whenever
 * the lock appears or clears, so a draft made before an archive or a dead
 * socket is dropped rather than parked behind disabled controls and saved
 * after a restore.
 */
export function editLockKey(lock: EditLock | null): string {
  return lock?.reason ?? "live";
}

export class EditLockedError extends Error {
  readonly lock: EditLock;

  constructor(lock: EditLock) {
    super(lock.message);
    this.name = "EditLockedError";
    this.lock = lock;
  }
}
